import { ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { getCssColor } from '../theme/colors'
import { useIframe } from '../hooks/useIframe'
import logoImage from '../assets/images/logo.png'

interface LayoutProps {
  children: ReactNode
}

export default function Layout({ children }: LayoutProps) {
  const location = useLocation()
  const { t } = useTranslation()
  const { config, customColors } = useIframe()

  const isActive = (path: string) => {
    return location.pathname === path
  }

  return (
    <div 
      className="h-screen safe-area-top flex flex-col"
      style={{ backgroundColor: getCssColor('mainBackground', customColors) }}
    >
      {/* Conditional Header */}
      {!config.hideHeader && (
        <header className="bg-white shadow-sm sticky top-0 z-50">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-4 sm:py-6">
              <Link to="/" className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">
                {t('app.name')}
              </Link>
              {!config.hideNavigation && (
                <div className="flex items-center space-x-2 sm:space-x-4 flex-shrink-0">
                  <nav className="flex space-x-1 sm:space-x-2">
                    <Link
                      to="/statistics"
                      className={`px-3 sm:px-4 py-2 rounded-full text-sm sm:text-base touch-manipulation min-h-[44px] flex items-center transition-colors ${
                        isActive('/statistics')
                          ? 'bg-green-700 text-green-50' 
                          : 'text-green-50 bg-green-900 hover:text-white hover:bg-green-800'
                      }`}
                      style={{
                        backgroundColor: isActive('/statistics') 
                          ? getCssColor('forest', customColors) 
                          : getCssColor('background', customColors)
                      }}
                    >
                      {t('navigation.statistics')}
                    </Link>
                    <Link
                      to="/map"
                      className={`px-3 sm:px-4 py-2 rounded-full text-sm sm:text-base touch-manipulation min-h-[44px] flex items-center transition-colors ${
                        isActive('/map')
                          ? 'bg-green-700 text-green-50'
                          : 'text-green-50 bg-green-900 hover:text-white hover:bg-green-800'
                      }`}
                      style={{
                        backgroundColor: isActive('/map') 
                          ? getCssColor('forest', customColors) 
                          : getCssColor('background', customColors)
                      }}
                    >
                      {t('navigation.map')}
                    </Link>
                  </nav>
                </div>
              )}
            </div>
          </div>
        </header>
      )}
      
      <main className={`flex-1 py-4 sm:px-6 sm:py-6 lg:px-8 flex flex-col min-h-0 ${config.hideFooter ? '' : 'pb-8'}`}>
        <div className={`${location.pathname === '/map' ? 'w-full' : 'max-w-7xl mx-auto'} flex-1 flex flex-col min-h-0`}>
          {children}
        </div>
      </main>
      
      {/* Conditional Footer */}
      {!config.hideFooter && (
        <footer className="fixed bottom-0 left-0 right-0 bg-white text-gray-800 py-2 z-40 shadow-lg border-t border-gray-200">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="text-center">
              <a 
                href="http://forests.digital" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="footer-link"
              >
                <img 
                  src={logoImage} 
                  alt="Forests Digital" 
                  className="footer-logo size-6"
                />
                Powered by<span style={{color: getCssColor('background', customColors)}}>Forests.Digital</span>
              </a>
            </div>
          </div>
        </footer>
      )}
    </div>
  )
}
